import React, { lazy, Suspense, useMemo } from 'react';
import { Switch, Redirect } from 'react-router-dom';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { AnalyticsInit, ErrorBoundary, ErrorComponents, useApplicationContext } from '@css-xp/base';
import { routePaths } from '@css-xp/ppc-constants';
import { ProxyGraphClients } from '@atlassian/temp-client-utils';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { BillingProfileDetailsPage } from '@css-xp/ppc-billing-profile-page';
import { CatalogDetailsPage, CataloguePage } from '@css-xp/ppc-catalogue-page';
import { CloudSitePage } from '@css-xp/ppc-cloud-site-page';
import { CustomerAddressDetailPage, CustomerAddressPage } from '@css-xp/ppc-customer-address-page';
import { AccountBalancePage, DetailPage } from '@css-xp/ppc-details-page';
import { HomePage } from '@css-xp/ppc-home-page';
import { ConfirmPaymentPage, InvoiceDetailsPage, InvoicesPage, RefundsPage } from '@css-xp/ppc-invoices-page';
import { PPCPageLoadExperienceKeys, withUFOWrapper } from '@css-xp/ppc-ufo';
import { EmailPage } from '@css-xp/ppc-email-page';
import { OrderDetailsPage } from '@css-xp/ppc-order-details-page';
import { PPCRouteWithAnalytics } from '@css-xp/ppc-route-with-analytics';
import { SearchPage } from '@css-xp/ppc-search-page';
import { SubscriptionsPage, InternalLicensesPage } from '@css-xp/ppc-subscriptions-page';
import { OrganisationDetailPage } from '@css-xp/ppc-organisation-page';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { CreditDetailsPage } from '@css-xp/ppc-credit-details-page';
import { NotificationPreferencePage } from '@css-xp/ppc-manage-notification-preferences';
import { DealRegistrationsPage } from '@css-xp/ppc-deal-registrations-page';
import { routesWithoutNavigations } from './routes-without-navigation';
import App from './App';
import { EntitlementPage, OrderPage, QuotesPage, QuoteDetailPage, CartPage } from './lazy-pages';
// @ts-ignore
// eslint-disable-next-line import/no-unresolved
const PapiOnboardingApp = lazy(() => import('papi_onboarding/App'));

const PapiOnboardingAppWrapped = () => {
  const user = useApplicationContext();
  return (
    <ErrorBoundary subject="papi-ui">
      <Suspense fallback={<></>}>
        <div className="onboarding-app-container">
          {/* @ts-ignore */}
          <PapiOnboardingApp user={user} />
        </div>
      </Suspense>
    </ErrorBoundary>
  );
};

const ApplicationRoutes = () => {
  const routes = useMemo(
    () => [
      ...routesWithoutNavigations,
      {
        source: AnalyticsEventSource.UNAUTHORIZED_SCREEN,
        exact: true,
        path: routePaths.unauthorized,
        component: withUFOWrapper(
          withErrorWrapper(ErrorComponents.Unauthorized),
          PPCPageLoadExperienceKeys.UnauthorizedErrorPage
        ),
        key: 'unauthorized',
      },
      {
        source: AnalyticsEventSource.ERROR_SCREEN,
        exact: true,
        path: routePaths.error,
        component: withUFOWrapper(
          withErrorWrapper(ErrorComponents.ServerError),
          PPCPageLoadExperienceKeys.GenericErrorPage
        ),
        key: 'error',
      },
      {
        source: AnalyticsEventSource.ERROR_SCREEN,
        exact: true,
        path: routePaths.errorWithType,
        component: withUFOWrapper(ErrorComponents.KnownServerError, PPCPageLoadExperienceKeys.KnownServerErrorPage),
        key: 'errorWithErrorType',
      },
      {
        source: AnalyticsEventSource.PARTNER_HOME_SCREEN,
        exact: true,
        path: routePaths.home,
        component: withUFOWrapper(HomePage, PPCPageLoadExperienceKeys.HomePage),
        key: 'home',
      },
      {
        source: AnalyticsEventSource.PARTNER_DETAILS_SCREEN,
        exact: true,
        path: routePaths.details,
        component: withUFOWrapper(DetailPage, PPCPageLoadExperienceKeys.DetailPage),
        key: 'AccountDetailPage',
      },
      {
        source: AnalyticsEventSource.PARTNER_ACCOUNT_BALANCE_SCREEN,
        exact: true,
        path: routePaths.accountBalance,
        component: withUFOWrapper(AccountBalancePage, PPCPageLoadExperienceKeys.AccountBalancePage),
        key: 'accountBalance',
      },
      {
        source: AnalyticsEventSource.PARTNER_INVOICES_SCREEN,
        exact: true,
        path: routePaths.invoices,
        component: withUFOWrapper(InvoicesPage, PPCPageLoadExperienceKeys.InvoicesPage),
        key: 'invoices',
      },
      {
        source: AnalyticsEventSource.PARTNER_CATALOG_DETAILS_SCREEN,
        exact: false,
        path: routePaths.catalogDetails,
        component: withUFOWrapper(CatalogDetailsPage, PPCPageLoadExperienceKeys.CatalogPage),
        key: 'catalogDetails',
      },
      {
        source: AnalyticsEventSource.PARTNER_INVOICE_DETAILS_SCREEN,
        exact: true,
        path: routePaths.invoiceDetails,
        component: withUFOWrapper(InvoiceDetailsPage, PPCPageLoadExperienceKeys.InvoiceDetailsPage),
        key: 'invoice-details',
      },
      {
        source: AnalyticsEventSource.PARTNER_CONFIRM_PAYMENT_SCREEN,
        exact: true,
        path: routePaths.confirmPayment,
        component: withUFOWrapper(ConfirmPaymentPage, PPCPageLoadExperienceKeys.ConfirmPaymentPage),
        key: 'confirm-payment',
      },
      {
        source: AnalyticsEventSource.PARTNER_REFUNDS_SCREEN,
        exact: true,
        path: routePaths.refundDetails,
        component: withUFOWrapper(RefundsPage, PPCPageLoadExperienceKeys.RefundsPage),
        key: 'refunds',
      },
      {
        source: AnalyticsEventSource.PARTNER_SUBSCRIPTIONS_SCREEN,
        exact: true,
        path: routePaths.subscriptions,
        component: withUFOWrapper(SubscriptionsPage, PPCPageLoadExperienceKeys.SubscriptionsPage),
        key: 'subscriptions',
      },
      {
        source: AnalyticsEventSource.PARTNER_INTERNAL_LICENSES_SCREEN,
        exact: true,
        path: routePaths.internalUsageSubscriptions,
        component: withUFOWrapper(InternalLicensesPage, PPCPageLoadExperienceKeys.InternalLicensesPage),
        key: 'internalUsageSubscriptions',
      },
      {
        source: AnalyticsEventSource.PARTNER_ORGANISATION_SCREEN,
        exact: true,
        path: routePaths.organisationDetails,
        component: withUFOWrapper(OrganisationDetailPage, PPCPageLoadExperienceKeys.OrganisationDetailPage),
        key: 'organisationDetails',
      },
      {
        source: AnalyticsEventSource.PARTNER_CATALOG_SCREEN,
        exact: true,
        path: routePaths.catalog,
        component: withUFOWrapper(CataloguePage, PPCPageLoadExperienceKeys.CatalogPage),
        key: 'catalogue',
        srcProxy: ProxyGraphClients.SXP,
      },
      {
        source: AnalyticsEventSource.PARTNER_ENTITLEMENT_SCREEN,
        exact: true,
        path: routePaths.entitlement,
        component: withUFOWrapper(EntitlementPage, PPCPageLoadExperienceKeys.EntitlementPage),
        key: 'entitlement',
      },
      {
        source: AnalyticsEventSource.PARTNER_SEARCH_SCREEN,
        exact: true,
        path: routePaths.search,
        component: withUFOWrapper(SearchPage, PPCPageLoadExperienceKeys.SearchPage),
        key: 'searchPage',
      },
      {
        source: AnalyticsEventSource.PARTNER_CUSTOMER_ADDRESS_SCREEN,
        exact: true,
        path: routePaths.customerAddresses,
        component: withUFOWrapper(CustomerAddressPage, PPCPageLoadExperienceKeys.CustomerAddressPage),
        key: 'addressPage',
      },
      {
        source: AnalyticsEventSource.PARTNER_BILLING_PROFILE_DETAILS_SCREEN,
        exact: true,
        path: routePaths.billingProfileDetails,
        component: withUFOWrapper(BillingProfileDetailsPage, PPCPageLoadExperienceKeys.BillingProfileDetailsPage),
        key: 'billingProfileDetails',
      },
      {
        source: AnalyticsEventSource.PARTNER_CUSTOMER_ADDRESS_DETAIL_SCREEN_BY_COMPANY,
        exact: true,
        path: routePaths.customerAddressDetailsByCompany,
        component: withUFOWrapper(CustomerAddressDetailPage, PPCPageLoadExperienceKeys.CustomerAddressDetailPage),
        key: 'addressDetailPageByCompany',
      },
      {
        source: AnalyticsEventSource.PARTNER_CUSTOMER_ADDRESS_DETAIL_SCREEN_BY_ID,
        exact: true,
        path: routePaths.customerAddressDetailsById,
        component: withUFOWrapper(CustomerAddressDetailPage, PPCPageLoadExperienceKeys.CustomerAddressDetailPage),
        key: 'addressDetailPageById',
      },
      {
        source: AnalyticsEventSource.PARTNER_EMAIL_DETAIL_SCREEN,
        exact: true,
        path: routePaths.emailDetails,
        component: withUFOWrapper(EmailPage, PPCPageLoadExperienceKeys.EmailPage),
        key: 'emailDetailById',
      },
      {
        source: AnalyticsEventSource.PARTNER_ORDER_DETAIL_SCREEN_BY_ID,
        exact: true,
        path: routePaths.orderDetails,
        component: withUFOWrapper(OrderDetailsPage, PPCPageLoadExperienceKeys.OrderDetailPage),
        key: 'orderDetailsPageById',
      },
      {
        source: AnalyticsEventSource.PARTNER_CLOUD_SITE_SCREEN,
        exact: true,
        path: routePaths.cloudSite,
        component: withUFOWrapper(CloudSitePage, PPCPageLoadExperienceKeys.CloudSitePage),
        key: 'cloudSitePage',
      },
      {
        source: AnalyticsEventSource.PARTNER_QUOTES_SCREEN,
        exact: true,
        path: routePaths.quotes,
        component: withUFOWrapper(QuotesPage, PPCPageLoadExperienceKeys.QuotesPage),
        key: 'quotesPage',
        // Since source proxy API is not the default ANS server, we are using `srcProxy: ProxyGraphClients.SXP` to
        // make sure that the SXP proxy API is used for this route.
        srcProxy: ProxyGraphClients.SXP,
      },
      {
        source: AnalyticsEventSource.PARTNER_QUOTES_DETAILS_SCREEN,
        exact: true,
        path: routePaths.ppcQuoteDetails,
        component: withUFOWrapper(QuoteDetailPage, PPCPageLoadExperienceKeys.QuoteDetailPage),
        key: 'quotesDetailsPage',
      },
      {
        source: AnalyticsEventSource.PARTNER_ONBOARDING_MANAGE_TOKEN_SCREEN,
        path: routePaths.manageToken,
        render() {
          return <PapiOnboardingAppWrapped />;
        },
        key: 'partnerOnboardingManagePage',
      },
      {
        source: AnalyticsEventSource.PARTNER_ONBOARDING_SCREEN,
        exact: true,
        path: routePaths.partnerOnboarding,
        render() {
          return <PapiOnboardingAppWrapped />;
        },
        key: 'partnerOnboardingPage',
      },
      {
        source: AnalyticsEventSource.ORDER_SCREEN,
        exact: true,
        path: routePaths.order,
        component: withUFOWrapper(OrderPage, PPCPageLoadExperienceKeys.OrderPage),
        key: 'order',
      },
      {
        source: AnalyticsEventSource.ORDER_SCREEN,
        exact: true,
        path: routePaths.cart,
        component: withUFOWrapper(CartPage, PPCPageLoadExperienceKeys.OrderPage),
        key: 'cart',
      },
      {
        source: AnalyticsEventSource.PARTNER_NOTIFICATION_PREFERENCE_SCREEN,
        exact: true,
        path: routePaths.manageNotifications,
        component: withUFOWrapper(NotificationPreferencePage, PPCPageLoadExperienceKeys.NotificationPreferencePage),
        key: 'notificationPreference',
      },
      {
        source: AnalyticsEventSource.PARTNER_CREDIT_DETAILS_SCREEN,
        exact: true,
        path: routePaths.creditDetails,
        component: withUFOWrapper(CreditDetailsPage, PPCPageLoadExperienceKeys.CreditDetailsPage),
        key: 'creditDetails',
      },
      {
        source: AnalyticsEventSource.PARTNER_DEAL_REGISTRATIONS_SCREEN,
        exact: true,
        path: routePaths.dealRegistrations,
        component: withUFOWrapper(DealRegistrationsPage, PPCPageLoadExperienceKeys.DealRegistrationsPage),
        key: 'dealRegistrations',
      },
      {
        source: AnalyticsEventSource.NOT_FOUND_SCREEN,
        exact: false,
        path: routePaths.notFound,
        component: withUFOWrapper(
          withErrorWrapper(ErrorComponents.NotFound),
          PPCPageLoadExperienceKeys.NotFoundErrorPage
        ),
        key: 'notFound',
      },
      {
        source: AnalyticsEventSource.NOT_FOUND_SCREEN,
        exact: false,
        path: routePaths.pathNotDefined,
        component: withUFOWrapper(
          withErrorWrapper(ErrorComponents.NotFound),
          PPCPageLoadExperienceKeys.NotFoundErrorPage
        ),
        key: 'pathNotDefined',
      },
    ],
    []
  );

  const redirectRoutes = useMemo(
    () => [
      {
        from: routePaths.base,
        to: routePaths.home,
        exact: true,
      },
    ],
    []
  );

  return (
    <App>
      <React.Suspense fallback={<LoadingIndicator />}>
        <AnalyticsInit />
        <Switch>
          {redirectRoutes.map((redirectRoute, idx) => (
            <Redirect key={idx} from={redirectRoute.from} to={redirectRoute.to} exact={redirectRoute.exact} />
          ))}
          {routes.map((route: any) => (
            <PPCRouteWithAnalytics
              key={route.key}
              source={route.source}
              exact={route.exact}
              path={route.path}
              component={route.component}
              render={route.render}
              srcProxy={route.srcProxy}
            />
          ))}
        </Switch>
      </React.Suspense>
    </App>
  );
};

export default <ApplicationRoutes />;
